import { useQuery } from "@tanstack/react-query";
import { api } from "../../shared/api/client";
import type { ModuleReach, ReleaseDetail } from "../../shared/types/api";
import {
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  InlineAlert,
  LoadingState,
  StatusBadge,
} from "../../shared/ui";

/**
 * Who else this component release touches.
 *
 * <p>Shown only on a component release, because only a component release has a blast radius
 * outside the project it was raised against. A team shipping Accounting 2.2 for one customer is
 * changing software four other customers run, and nothing else on the release page says so — the
 * scope tab lists work items, the journey lists environments, and neither mentions the other
 * projects at all.
 *
 * <p>It reads projects, not deployments: this is who <em>could</em> be affected, and which of them
 * are on which version today. Releasing does not deploy for any of them, and the list makes the
 * difference visible rather than leaving somebody to assume one way or the other.
 */
export function ComponentReachCard({ release }: { release: ReleaseDetail }) {
  const reach = useQuery({
    queryKey: ["module-reach", release.module?.id],
    queryFn: () => api.get<ModuleReach[]>(`/api/v1/modules/${release.module?.id}/projects`),
    enabled: release.module != null,
  });

  if (release.releaseScope !== "COMPONENT" || release.module == null) {
    return null;
  }

  const rows = reach.data ?? [];
  const others = rows.filter((row) => row.projectId !== release.projectId);
  const behind = others.filter((row) => !row.onLatest);

  return (
    <Card>
      <CardHeader
        title="Who else runs this"
        subtitle={`${release.module.name} is shared. Releasing it here does not deploy it for anybody else.`}
      />
      <CardBody className="space-y-3">
        {behind.length > 0 && (
          <InlineAlert tone="warning">
            {behind.length === 1
              ? "One other project is not on the latest release of this module."
              : `${behind.length} other projects are not on the latest release of this module.`}{" "}
            Each moves when it is actually deployed for that customer.
          </InlineAlert>
        )}

        {reach.isPending ? (
          <LoadingState label="Loading projects…" />
        ) : others.length === 0 ? (
          <EmptyState
            title="Only this project"
            message={`Nobody else has ${release.module.name} linked yet, so this release affects only ${release.projectName}.`}
          />
        ) : (
          <ul className="divide-y divide-ink-100 rounded-lg border border-ink-200">
            {others.map((row) => (
              <li
                key={row.projectId}
                className="flex items-center justify-between gap-3 px-3 py-2.5"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-ink-900">{row.projectName}</p>
                  <p className="font-mono text-xs text-ink-400">{row.projectKey}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="tnum font-mono text-sm text-ink-700">
                    {row.currentVersion ?? "—"}
                  </span>
                  <StatusBadge tone={row.onLatest ? "green" : "amber"}>
                    {row.onLatest ? "Current" : "Behind"}
                  </StatusBadge>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
